<template>
  <v-dialog :value="modalStatus" max-width="800" scrollable @input="closeModal">
    <v-card>
      <!-- Header -->
      <v-card-title class="modal-header pa-5">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-3" size="28">mdi-tag-multiple-outline</v-icon>
          <div>
            <div class="text-h6 font-weight-medium">Suscripción a Especialidades</div>
            <div class="text-caption grey--text text--darken-1">
              Selecciona las especialidades que quieres seguir en este proyecto
            </div>
          </div>
        </div>
        <v-spacer />
        <!-- Select all global -->
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              small
              :outlined="!allSelected"
              :color="allSelected ? 'primary' : 'grey darken-1'"
              class="mr-3 select-all-btn"
              v-bind="attrs"
              v-on="on"
              @click="toggleAll"
            >
              <v-icon left small>{{ allSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline' }}</v-icon>
              {{ allSelected ? 'Deseleccionar todo' : 'Seleccionar todo' }}
            </v-btn>
          </template>
          <span>{{ allSelected ? 'Quitar todas las suscripciones' : 'Suscribirse a todas las especialidades' }}</span>
        </v-tooltip>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- Project info bar -->
      <div class="project-info-bar px-5 py-3 d-flex align-center">
        <v-icon small color="grey darken-1" class="mr-2">mdi-briefcase-outline</v-icon>
        <div>
          <span class="text-body-2 font-weight-medium">{{ selectedProject && selectedProject.name }}</span>
          <span v-if="selectedProject && selectedProject.companyName" class="text-caption grey--text ml-2">
            — {{ selectedProject.companyName }}
          </span>
        </div>
        <v-spacer />
        <v-chip x-small outlined color="primary" class="ml-2">
          <v-icon left x-small>mdi-check-circle-outline</v-icon>
          {{ selected.length }} / {{ specialties.length }} seleccionadas
        </v-chip>
      </div>

      <v-divider />

      <!-- Column legend -->
      <div class="legend-bar px-5 py-2">
        <div class="specialty-grid">
          <div class="col-name">
            <span class="text-caption grey--text text--darken-1 font-weight-medium text-uppercase" style="letter-spacing: 0.05em">
              Especialidad
            </span>
          </div>
          <div class="col-check d-flex justify-center">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="type-header d-flex flex-column align-center"
                  @click="toggleAll"
                >
                  <v-icon small color="#5C6BC0" class="mb-1">mdi-bell-check-outline</v-icon>
                  <span class="notif-label" style="color: #5C6BC0">Suscrito</span>
                  <v-icon x-small class="mt-1" color="#5C6BC0">
                    {{ allSelected ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
                  </v-icon>
                </div>
              </template>
              <span>{{ allSelected ? 'Quitar todas las suscripciones' : 'Suscribirse a todas las especialidades' }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>

      <v-divider />

      <!-- Specialty rows -->
      <v-card-text class="pa-0" style="max-height: 420px; overflow-y: auto;">

        <div v-if="loading" class="pa-10 d-flex flex-column align-center justify-center">
          <v-progress-circular indeterminate color="primary" size="36" />
          <span class="text-caption grey--text mt-3">Cargando especialidades...</span>
        </div>

        <template v-else>
          <div
            v-for="(specialty, index) in specialties"
            :key="specialty.id"
            class="specialty-row px-5"
            :class="{ 'row-alt': index % 2 === 1, 'row-selected': selected.includes(specialty.id) }"
            @click="toggleSpecialty(specialty.id)"
          >
            <div class="specialty-grid align-center">
              <div class="col-name d-flex align-center" style="min-width:0">
                <v-icon
                  small
                  class="mr-2 flex-shrink-0"
                  :color="selected.includes(specialty.id) ? '#5C6BC0' : 'grey lighten-1'"
                >
                  mdi-tag-outline
                </v-icon>
                <span
                  class="text-body-2 text-truncate"
                  :class="selected.includes(specialty.id) ? 'font-weight-medium' : ''"
                >
                  {{ specialty.name }}
                </span>
              </div>
              <div class="col-check d-flex justify-center align-center" @click.stop>
                <v-checkbox
                  v-model="selected"
                  :value="specialty.id"
                  color="#5C6BC0"
                  dense
                  hide-details
                  class="ma-0 pa-0 checkbox-center"
                />
              </div>
            </div>
          </div>

          <div v-if="!specialties.length" class="pa-10 text-center grey--text">
            <v-icon size="40" color="grey lighten-2" class="mb-3 d-block">mdi-tag-off-outline</v-icon>
            No hay especialidades disponibles para este proyecto
          </div>
        </template>
      </v-card-text>

      <v-divider />

      <!-- Footer -->
      <v-card-actions class="pa-4">
        <div class="d-flex align-center">
          <v-icon small class="mr-1" color="grey darken-1">mdi-information-outline</v-icon>
          <span class="text-caption grey--text">
            En RDIs recibirás notificaciones SÓLO de las especialidades seleccionadas
          </span>
        </div>
        <v-spacer />
        <v-btn text class="mr-2" @click="closeModal">Cancelar</v-btn>
        <v-btn
          color="primary"
          elevation="0"
          :loading="saving"
          @click="save"
        >
          <v-icon left small>mdi-content-save-outline</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getProjectSpecialties, setUserSpecialtySubscriptions } from '@/helpers/api/project'

export default {
  name: 'SpecialtySubscriptionModal',
  props: {
    modalStatus:     { type: Boolean,  required: true },
    closeModal:      { type: Function, required: true },
  },
  data() {
    return {
      specialties: [],
      selected:    [],
      loading:     false,
      saving:      false,
    }
  },
  computed: {
		selectedProject(){
			return this.$store.projectsStore.state.selectedProject
		},
    allSelected() {
      return this.specialties.length > 0 &&
        this.specialties.every(s => this.selected.includes(s.id))
    },
		userProjectSpecialtiesSubscriptions() {
			return this.$store.projectsStore.state.userProjectSpecialtiesSubscriptions
		},
  },
  watch: {
    modalStatus(val) {
      if (val) this.init()
    },
		userProjectSpecialtiesSubscriptions(val) {
			if(val){
				this.selected = val.map(s => s.specialty_id)
			}
		},
  },
  mounted() {
    if (this.modalStatus) this.init()
		if(this.userProjectSpecialtiesSubscriptions){
			this.selected = this.userProjectSpecialtiesSubscriptions.map(s => s.specialty_id)
		}
  },
  methods: {
    async init() {
      if (!this.selectedProject) return
      this.loading = true
      try {
        const res = await getProjectSpecialties({ project_id: this.selectedProject.id })
        this.specialties = res.specialties || res || []
      } catch (e) {
        this.specialties = []
        this.selected    = []
      } finally {
        this.loading = false
      }
    },
    toggleAll() {
      if (this.allSelected) {
        this.selected = []
      } else {
        this.selected = this.specialties.map(s => s.id)
      }
    },
    toggleSpecialty(id) {
      const idx = this.selected.indexOf(id)
      if (idx === -1) {
        this.selected.push(id)
      } else {
        this.selected.splice(idx, 1)
      }
    },
    async save() {
      if (!this.selectedProject) return
      this.saving = true
      try {
        await setUserSpecialtySubscriptions({
          project_id:    this.selectedProject.id,
          specialty_ids: this.selected,
        })
        this.closeModal()
				this.$store.projectsStore.dispatch('getSelectedProject')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.modal-header {
  background: #FAFAFA;
}

.project-info-bar {
  background: #EEF2FF;
  border-left: 3px solid #5C6BC0;
}

.legend-bar {
  background: #F5F5F5;
}

.specialty-grid {
  display: grid;
  grid-template-columns: 1fr 100px;
  align-items: center;
  width: 100%;
}

.col-name {
  min-width: 0;
  padding-right: 8px;
}

.col-check {
  min-width: 0;
  text-align: center;
}

.notif-label {
  font-size: 10px;
  line-height: 1.25;
  text-align: center;
  white-space: pre-line;
  word-break: break-word;
  display: block;
}

.type-header {
  cursor: pointer;
  border-radius: 6px;
  padding: 4px 10px;
  transition: background 0.15s;
}
.type-header:hover {
  background: #EEEEEE;
}

.specialty-row {
  min-height: 52px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.15s;
}

.specialty-row:hover {
  background: #F3F6FF;
}

.row-alt {
  background: #FAFAFA;
}

.row-alt:hover {
  background: #F3F6FF;
}

.row-selected {
  background: #EEF2FF !important;
}

.row-selected:hover {
  background: #E8EDFF !important;
}

.select-all-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
}

.checkbox-center {
  display: flex !important;
  justify-content: center;
}

.checkbox-center >>> .v-input__control {
  width: auto !important;
}
</style>